import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useTheme } from '../contexts/ThemeContext';
import FormSelect from './FormSelect';
import ButtonGroup from './ButtonGroup';
import LoadingSpinner from './LoadingSpinner';
import EnhancedLoadingState, { LoadingSkeleton } from './EnhancedLoadingState';
import WizardTransition, { FadeTransition, ScaleTransition } from './WizardTransition';
import EnhancedTooltip, { HelpTooltip } from './EnhancedTooltip';
import { ApiClient } from '../utils/apiClient';
import { getWizardLayoutClasses, getVoiceSettingsLayout, getTouchFriendlyClasses, getResponsiveButtonClasses } from '../utils/responsiveUtils';

/**
 * Step 2 Voice Configuration Component
 * Handles voice selection, creativity level, and voice settings configuration
 */
const Step2VoiceConfig = ({
  formData = {},
  onFormDataChange,
  onNext,
  onSaveAndExit,
  onBack,
  errors = {},
  isLoading = false,
  className = '',
  isTransitioning = false,
  transitionDirection = 'forward'
}) => {
  const { getToken } = useAuth();
  const { theme } = useTheme();
  const apiClient = useRef(new ApiClient(getToken)).current;

  // Local state
  const [voices, setVoices] = useState([]);
  const [loadingVoices, setLoadingVoices] = useState(true);
  const [previewingVoice, setPreviewingVoice] = useState(null);
  const [localFormData, setLocalFormData] = useState({
    voice_id: '',
    creativity_level: 'creative',
    stability: 'medium',
    similarity_boost: 'medium',
    style: 'medium',
    ...formData
  });
  const [validationErrors, setValidationErrors] = useState({});

  // Enhanced UX features
  const touchFriendlyClasses = getTouchFriendlyClasses();
  const layoutClasses = getWizardLayoutClasses();
  const voiceLayoutClasses = getVoiceSettingsLayout();

  /**
   * Check if form is valid for proceeding
   */
  const canProceed = () => {
    return Object.keys(validationErrors).length === 0 &&
      localFormData.voice_id &&
      localFormData.creativity_level &&
      localFormData.stability &&
      localFormData.similarity_boost &&
      localFormData.style;
  };

  // Button value mappings from design document
  const creativityLevelMap = {
    "deterministic": { temperature: 0.0 },
    "creative": { temperature: 0.5 },
    "more_creative": { temperature: 1.0 }
  };

  const voiceSettingsMap = {
    stability: {
      "low": 0.2,
      "medium": 0.5,
      "high": 0.8
    },
    similarity_boost: {
      "low": 0.3,
      "medium": 0.6,
      "high": 0.9
    },
    style: {
      "low": 0.1,
      "medium": 0.4,
      "high": 0.7
    }
  };

  // Button options
  const creativityOptions = [
    { value: 'deterministic', label: 'Deterministic' },
    { value: 'creative', label: 'Creative' },
    { value: 'more_creative', label: 'More Creative' }
  ];

  const voiceSettingOptions = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' }
  ];

  // Load voices on mount
  useEffect(() => {
    fetchVoices();
  }, []);

  // Update local form data when props change
  useEffect(() => {
    setLocalFormData(prev => ({
      ...prev,
      ...formData
    }));
  }, [formData]);

  // Validate form in real-time
  useEffect(() => {
    validateForm();
  }, [localFormData]);

  /**
   * Fetch available voices from the API
   */
  const fetchVoices = async () => {
    try {
      setLoadingVoices(true);
      const voiceList = await apiClient.get('/api/voices');
      console.log('Voices API response:', voiceList);
      setVoices(voiceList);

      // Set default voice if none selected and no existing voice in formData
      if (!localFormData.voice_id && !formData.voice_id && voiceList.length > 0) {
        const defaultVoice = voiceList[0]; // Use first voice as default
        handleInputChange({
          target: {
            name: 'voice_id',
            value: defaultVoice.service_voice_id
          }
        });
      }
    } catch (error) {
      console.error('Failed to fetch voices:', error);
      // Set fallback voices if API fails
      setVoices([
        {
          service_voice_id: 'fallback-voice-1',
          parrotmate_name: 'Default Voice',
          short_description: 'Professional voice for business use'
        }
      ]);
    } finally {
      setLoadingVoices(false);
    }
  };

  /**
   * Handle form field changes
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    const updatedData = {
      ...localFormData,
      [name]: value
    };

    setLocalFormData(updatedData);

    // Clear field-specific errors
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Notify parent component
    if (onFormDataChange) {
      onFormDataChange(updatedData);
    }
  };

  /**
   * Handle voice preview
   */
  const handleVoicePreview = async (voiceId) => {
    if (previewingVoice === voiceId) {
      // Stop preview
      setPreviewingVoice(null);
      return;
    }

    try {
      setPreviewingVoice(voiceId);

      // Get current voice settings for preview
      const currentSettings = {
        stability: voiceSettingsMap.stability[localFormData.stability],
        similarity_boost: voiceSettingsMap.similarity_boost[localFormData.similarity_boost],
        style: voiceSettingsMap.style[localFormData.style]
      };

      // Find the voice data to get the preview audio file
      const voice = voices.find(v => v.service_voice_id === voiceId);
      console.log('Found voice for preview:', voice);

      if (voice && voice.preview_audio_file) {
        // Play the preview audio file - use full API URL
        const audioUrl = `${apiClient.baseURL}/api/voices/preview/${voice.preview_audio_file}`;
        console.log('Attempting to play audio from URL:', audioUrl);
        const audio = new Audio(audioUrl);

        audio.onloadeddata = () => {
          console.log('Audio loaded successfully, starting playback');
          audio.play().catch(error => {
            console.error('Failed to play audio:', error);
            setPreviewingVoice(null);
          });
        };

        audio.onended = () => {
          console.log('Audio playback ended');
          setPreviewingVoice(null);
        };

        audio.onerror = (error) => {
          console.error('Audio failed to load:', error);
          console.error('Audio URL that failed:', audioUrl);
          setPreviewingVoice(null);
        };

        // Reset preview state after 10 seconds as fallback
        setTimeout(() => {
          setPreviewingVoice(null);
        }, 10000);
      } else {
        console.log('No preview audio file available for voice:', voiceId);
        console.log('Voice object:', voice);
        setPreviewingVoice(null);
      }

    } catch (error) {
      console.error('Failed to preview voice:', error);
      setPreviewingVoice(null);
    }
  };

  /**
   * Validate form fields
   */
  const validateForm = () => {
    const newErrors = {};

    // Voice selection validation
    if (!localFormData.voice_id) {
      newErrors.voice_id = 'Voice selection is required';
    }

    // Creativity level validation
    if (!localFormData.creativity_level || !creativityLevelMap[localFormData.creativity_level]) {
      newErrors.creativity_level = 'Creativity level selection is required';
    }

    // Voice settings validation
    if (!localFormData.stability || !voiceSettingsMap.stability[localFormData.stability]) {
      newErrors.stability = 'Stability setting is required';
    }

    if (!localFormData.similarity_boost || !voiceSettingsMap.similarity_boost[localFormData.similarity_boost]) {
      newErrors.similarity_boost = 'Similarity boost setting is required';
    }

    if (!localFormData.style || !voiceSettingsMap.style[localFormData.style]) {
      newErrors.style = 'Style setting is required';
    }

    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle continue to Step 3
   */
  const handleContinue = (e) => {
    e.preventDefault();

    if (validateForm() && onNext) {
      // Include mapped values for backend processing
      const finalData = {
        ...localFormData,
        temperature: creativityLevelMap[localFormData.creativity_level].temperature,
        voice_settings: {
          stability: voiceSettingsMap.stability[localFormData.stability],
          similarity_boost: voiceSettingsMap.similarity_boost[localFormData.similarity_boost],
          style: voiceSettingsMap.style[localFormData.style]
        }
      };
      onNext(finalData);
    }
  };

  /**
   * Handle save and exit
   */
  const handleSaveAndExit = (e) => {
    e.preventDefault();

    if (validateForm() && onSaveAndExit) {
      // Include mapped values for backend processing
      const finalData = {
        ...localFormData,
        temperature: creativityLevelMap[localFormData.creativity_level].temperature,
        voice_settings: {
          stability: voiceSettingsMap.stability[localFormData.stability],
          similarity_boost: voiceSettingsMap.similarity_boost[localFormData.similarity_boost],
          style: voiceSettingsMap.style[localFormData.style]
        }
      };
      onSaveAndExit(finalData);
    }
  };

  // Convert voices to select options
  const voiceOptions = voices.map(voice => ({
    value: voice.service_voice_id,
    label: `${voice.parrotmate_name}${voice.short_description ? ` - ${voice.short_description}` : ''}`
  }));

  return (
    <div className={`step2-voice-config ${className}`}>
      {/* Header */}
      <div className="text-center mb-8">
        <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6 ${theme === 'dark' ? 'bg-teal-900' : 'bg-teal-100'
          }`}>
          <svg className={`w-10 h-10 ${theme === 'dark' ? 'text-teal-400' : 'text-teal-600'
            }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
          </svg>
        </div>
        <h2 className={`text-2xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
          Voice Configuration
        </h2>
        <p className={`text-lg ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
          }`}>
          Choose your parrot's voice and configure its speaking style
        </p>
      </div>

      <form className="space-y-8">
        {/* Voice Selection Section */}
        <div className={`rounded-lg p-6 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'
          }`}>
          <h3 className={`text-xl font-semibold mb-6 ${theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
            Voice Selection
          </h3>

          {loadingVoices ? (
            <LoadingSkeleton type="form" count={3} />
          ) : (
            <div className="space-y-4">
              <FormSelect
                label="Choose Voice"
                name="voice_id"
                options={voiceOptions}
                placeholder="Select a voice"
                required={true}
                value={localFormData.voice_id}
                onChange={handleInputChange}
                error={validationErrors.voice_id || errors.voice_id}
                hasError={!!(validationErrors.voice_id || errors.voice_id)}
                helpText="Select the voice that will represent your business"
                disabled={isLoading}
              />

              {/* Voice Preview */}
              {localFormData.voice_id && (
                <FadeTransition show={true}>
                  <div className="flex items-center space-x-4">
                    <button
                      type="button"
                      onClick={() => handleVoicePreview(localFormData.voice_id)}
                      disabled={isLoading || previewingVoice || isTransitioning}
                      className={`${getResponsiveButtonClasses('md')} rounded-lg font-medium transition-all duration-200 ${touchFriendlyClasses.button} ${previewingVoice === localFormData.voice_id
                        ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                        : theme === 'dark'
                          ? 'bg-teal-600 hover:bg-teal-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105 focus:ring-teal-500'
                          : 'bg-teal-600 hover:bg-teal-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105 focus:ring-teal-500'
                        } focus:outline-none focus:ring-2 focus:ring-offset-2`}
                    >
                      {previewingVoice === localFormData.voice_id ? (
                        <div className="flex items-center">
                          <LoadingSpinner size="sm" />
                          <span className="ml-2">Playing...</span>
                        </div>
                      ) : (
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M19 10a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>Preview Voice</span>
                        </div>
                      )}
                    </button>
                    <div className="flex items-center space-x-2">
                      <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                        Preview how this voice will sound with your current settings
                      </p>
                      <HelpTooltip
                        content="Voice previews use your current quality settings to give you an accurate representation of how your parrot will sound."
                        position="top"
                      />
                    </div>
                  </div>
                </FadeTransition>
              )}
            </div>
          )}
        </div>

        {/* Creativity Level Section */}
        <div className={`${layoutClasses.section} ${theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'
          }`}>
          <div className="flex items-center space-x-2 mb-6">
            <h3 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
              AI Behavior
            </h3>
            <HelpTooltip
              content="This setting controls how creative and varied your AI's responses will be. Deterministic provides consistent responses, while More Creative allows for more varied and spontaneous interactions."
              position="top"
            />
          </div>

          <ButtonGroup
            label="Creativity Level"
            name="creativity_level"
            options={creativityOptions}
            value={localFormData.creativity_level}
            onChange={handleInputChange}
            required={true}
            error={validationErrors.creativity_level || errors.creativity_level}
            hasError={!!(validationErrors.creativity_level || errors.creativity_level)}
            helpText="Controls how creative and varied the AI responses will be"
            disabled={isLoading || isTransitioning}
          />
        </div>

        {/* Voice Settings Section */}
        <div className={`${layoutClasses.section} ${theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'
          }`}>
          <div className="flex items-center space-x-2 mb-6">
            <h3 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
              Voice Quality Settings
            </h3>
            <HelpTooltip
              content="These settings fine-tune the voice quality. Experiment with different combinations to find the perfect sound for your business."
              position="top"
            />
          </div>

          <div className={voiceLayoutClasses.grid}>
            <div className="space-y-4">
              <ButtonGroup
                label="Stability"
                name="stability"
                options={voiceSettingOptions}
                value={localFormData.stability}
                onChange={handleInputChange}
                required={true}
                error={validationErrors.stability || errors.stability}
                hasError={!!(validationErrors.stability || errors.stability)}
                helpText="Higher values make speech more consistent but potentially monotone"
                disabled={isLoading || isTransitioning}
              />
              <div className="text-xs text-gray-500 dark:text-gray-400">
                <strong>Low:</strong> More expressive, <strong>High:</strong> More consistent
              </div>
            </div>

            <div className="space-y-4">
              <ButtonGroup
                label="Similarity Boost"
                name="similarity_boost"
                options={voiceSettingOptions}
                value={localFormData.similarity_boost}
                onChange={handleInputChange}
                required={true}
                error={validationErrors.similarity_boost || errors.similarity_boost}
                hasError={!!(validationErrors.similarity_boost || errors.similarity_boost)}
                helpText="Higher values boost clarity and consistency of the voice"
                disabled={isLoading || isTransitioning}
              />
              <div className="text-xs text-gray-500 dark:text-gray-400">
                <strong>Low:</strong> More natural, <strong>High:</strong> More clear
              </div>
            </div>

            <div className="space-y-4">
              <ButtonGroup
                label="Style"
                name="style"
                options={voiceSettingOptions}
                value={localFormData.style}
                onChange={handleInputChange}
                required={true}
                error={validationErrors.style || errors.style}
                hasError={!!(validationErrors.style || errors.style)}
                helpText="Controls the expressiveness and style of the voice"
                disabled={isLoading || isTransitioning}
              />
              <div className="text-xs text-gray-500 dark:text-gray-400">
                <strong>Low:</strong> Neutral tone, <strong>High:</strong> More expressive
              </div>
            </div>
          </div>
        </div>



        {/* Form Actions */}
        <div className={`${layoutClasses.actions} pt-6`}>
          <button
            type="button"
            onClick={onBack}
            className={`${getResponsiveButtonClasses('md')} border rounded-lg font-medium transition-colors ${touchFriendlyClasses.button} ${theme === 'dark'
              ? 'border-gray-600 text-gray-300 hover:bg-gray-700 focus:ring-gray-500'
              : 'border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-gray-500'
              } focus:outline-none focus:ring-2 focus:ring-offset-2`}
            disabled={isLoading || isTransitioning}
          >
            <div className="flex items-center">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span>Back to Step 1</span>
            </div>
          </button>

          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
            <button
              type="button"
              onClick={handleSaveAndExit}
              className={`${getResponsiveButtonClasses('md')} border rounded-lg font-medium transition-colors ${touchFriendlyClasses.button} ${canProceed() && !isLoading && !isTransitioning
                ? theme === 'dark'
                  ? 'border-gray-600 text-gray-300 hover:bg-gray-700 focus:ring-gray-500'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-gray-500'
                : 'border-gray-400 text-gray-400 cursor-not-allowed'
                } focus:outline-none focus:ring-2 focus:ring-offset-2`}
              disabled={!canProceed() || isLoading || isTransitioning}
            >
              {isLoading || isTransitioning ? (
                <div className="flex items-center">
                  <LoadingSpinner size="sm" />
                  <span className="ml-2">Saving...</span>
                </div>
              ) : (
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <span>Save and Exit</span>
                </div>
              )}
            </button>

            <button
              type="button"
              onClick={handleContinue}
              className={`${getResponsiveButtonClasses('md')} rounded-lg font-medium transition-all duration-200 ${touchFriendlyClasses.button} ${canProceed() && !isLoading && !isTransitioning
                ? 'bg-teal-600 hover:bg-teal-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105 focus:ring-teal-500'
                : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                } focus:outline-none focus:ring-2 focus:ring-offset-2`}
              disabled={!canProceed() || isLoading || isTransitioning}
            >
              {isLoading || isTransitioning ? (
                <div className="flex items-center">
                  <LoadingSpinner size="sm" />
                  <span className="ml-2">
                    {isTransitioning ? 'Transitioning...' : 'Processing...'}
                  </span>
                </div>
              ) : (
                <div className="flex items-center">
                  <span>Continue to Step 3</span>
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Step2VoiceConfig;
